import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { authGet } from '../../utils/auth';
import axios from 'axios';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const API = '/api';

const AdminLogin = () => {
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already authenticated
    const checkAuth = async () => {
      try {
        const response = await authGet('/auth/me');
        
        if (response.data && response.data.user_id) {
          // User is authenticated
          if (response.data.role === 'admin') {
            navigate('/admin', { replace: true });
          } else {
            navigate('/admin/pending', { replace: true });
          }
          return;
        }
      } catch (error) {
        // Not authenticated — server will have already 401'd; no client-side
        // state to clear now that session lives entirely in the httpOnly cookie.
      }
      setLoading(false);
    };

    checkAuth();
  }, [navigate]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('Please enter a password');
      return;
    }

    setLoginLoading(true);
    try {
      // withCredentials ensures the server-set httpOnly session_token cookie is stored.
      const response = await axios.post(
        `${API}/auth/password-login`,
        { password },
        { withCredentials: true },
      );

      if (response.data.success) {
        toast.success('Login successful');
        navigate('/admin', { replace: true, state: { user: response.data.user } });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid password');
    }
    setLoginLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-card border border-border rounded-xl p-8 shadow-sm">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <LogIn className="w-8 h-8 text-primary" strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground mb-2" data-testid="admin-login-heading">
              Admin Login
            </h1>
            <p className="text-sm text-muted-foreground">Sign in to access the admin area</p>
          </div>
          
          {/* Password Login Form */}
          <form onSubmit={handlePasswordLogin} className="mb-6">
            <div className="relative mb-4">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <label htmlFor="admin-password" className="sr-only">Admin password</label>
              <input
                id="admin-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                aria-label="Admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full pl-10 pr-10 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 bg-background text-foreground"
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-lg px-6 py-3 font-medium transition-all disabled:opacity-50"
              data-testid="password-login-btn"
            >
              {loginLoading ? 'Signing in...' : 'Sign in with Password'}
            </button>
          </form>
          
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
